import React, { useState } from "react";
import HealthMonitorApp from "./component/HealthMonitorApp";
import FaceAuth from "./component/FaceAuth";
import ReportsPage from "./component/ReportsPage";
import {
  Heart,
  ArrowLeft,
  Activity as ActivityIcon,
  AlertCircle,
  FileText,
} from "lucide-react";
import "./App.css";

function U4RadLogo({ size = 28, className = "" }) {
  return (
    <img
      src="/logo192.png"
      alt="U4Rad Logo"
      width={size}
      height={size}
      className={className}
      style={{ objectFit: "contain" }}
    />
  );
}

function App() {
  const [currentPage, setCurrentPage] = useState("face-auth");

  const handleLoginSuccess = () => setCurrentPage("home");

  if (currentPage === "face-auth") {
    return (
      <div className="app-root">
        <FaceAuth onLoginSuccess={handleLoginSuccess} />
      </div>
    );
  }

  if (currentPage === "home") {
    return (
      <div className="app-root">
        <header className="app-header">
          <div className="header-inner">
            <div className="header-brand">
              <U4RadLogo size={56} />
              <div className="brand-text">
                <span className="brand-name">AI Vitals</span>
                <span className="brand-tag">Powered by U4Rad AI</span>
              </div>
            </div>
            <div className="header-status">
              <span className="header-status-dot" />
              <span className="header-status-text">System Online</span>
            </div>
          </div>
        </header>

        <main className="app-main">
          <div className="landing-container">
            <div className="landing-hero">
              <div className="hero-badge">
                <ActivityIcon size={14} />
                AI-Powered Health Monitoring
              </div>
              <h1 className="hero-title">
                AI Vital <br />
                <span className="hero-highlight">Screening Platform</span>
              </h1>
              <p className="hero-subtitle">
                AI-powered vital sign screening using simple camera-based technology
              </p>
            </div>

            <div className="navigation-grid">
              <div className="nav-card" onClick={() => setCurrentPage("health")}>
                <div className="nav-card-accent nav-card-accent-health" />
                <div className="nav-card-inner">
                  <div className="nav-icon-wrap nav-icon-health">
                    <Heart size={24} />
                  </div>
                  <div className="nav-card-content">
                    <h2 className="nav-card-title">VitalScan</h2>
                    <p className="nav-card-desc">
                      Monitor heart rate, HRV, blood pressure, SpO2, and more
                      using AI-powered rPPG technology.
                    </p>
                    <div className="nav-tags">
                      <span className="nav-tag">Heart Rate</span>
                      <span className="nav-tag">HRV Analysis</span>
                      <span className="nav-tag">Blood Pressure</span>
                      <span className="nav-tag">SpO2</span>
                    </div>
                  </div>
                  <div className="nav-arrow">&rarr;</div>
                </div>
              </div>

              <div className="nav-card" onClick={() => setCurrentPage("reports")}>
                <div className="nav-card-accent nav-card-accent-height" />
                <div className="nav-card-inner">
                  <div className="nav-icon-wrap nav-icon-height">
                    <FileText size={24} />
                  </div>
                  <div className="nav-card-content">
                    <h2 className="nav-card-title">Reports</h2>
                    <p className="nav-card-desc">
                      View and download your generated AI vital sign reports,
                      complete with patient information and measurements.
                    </p>
                    <div className="nav-tags">
                      <span className="nav-tag">Download PDF</span>
                      <span className="nav-tag">Patient Details</span>
                      <span className="nav-tag">History</span>
                    </div>
                  </div>
                  <div className="nav-arrow">&rarr;</div>
                </div>
              </div>
            </div>

            <div className="persistent-disclaimer">
              <AlertCircle size={16} className="disclaimer-icon" />
              <p>
                <strong>Disclaimer:</strong> The displayed vital signs are
                AI-generated estimates intended for informational purposes only.
                They are not intended for diagnosis, treatment, monitoring, or
                prevention of any disease. Always consult a qualified healthcare
                professional for medical decisions.
              </p>
            </div>
          </div>
        </main>

        <footer className="app-footer">
          <div className="footer-inner">
            <div className="footer-brand">
              <U4RadLogo size={20} />
              <span className="footer-brand-name">U4Rad AI Vitals</span>
            </div>
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} U4Rad Technologies. All rights reserved. For
              informational and wellness purposes only. Not a medical device.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  if (currentPage === "health") {
    return (
      <div className="app-root">
        <header className="app-header">
          <div className="header-inner">
            <div className="header-left">
              <button className="header-back" onClick={() => setCurrentPage("home")}>
                <ArrowLeft size={18} />
              </button>
              <div className="header-brand header-brand-sm">
                <U4RadLogo size={36} />
                <span className="brand-name brand-name-sm">U4Rad AI Vitals</span>
              </div>
            </div>
            <div className="header-status">
              <span className="header-status-dot" />
              <span className="header-status-text">VitalScan Active</span>
            </div>
          </div>
        </header>
        <main className="app-main app-main-compact">
          <HealthMonitorApp />
        </main>
        <footer className="app-footer">
          <div className="footer-inner">
            <div className="footer-brand">
              <U4RadLogo size={20} />
              <span className="footer-brand-name">U4Rad AI Vitals</span>
            </div>
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} U4Rad Technologies. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  if (currentPage === "reports") {
    return (
      <div className="app-root">
        <header className="app-header">
          <div className="header-inner">
            <div className="header-left">
              <button className="header-back" onClick={() => setCurrentPage("home")}>
                <ArrowLeft size={18} />
              </button>
              <div className="header-brand header-brand-sm">
                <U4RadLogo size={36} />
                <span className="brand-name brand-name-sm">U4Rad AI Vitals</span>
              </div>
            </div>
            <div className="header-status">
              <span className="header-status-dot" />
              <span className="header-status-text">Reports</span>
            </div>
          </div>
        </header>
        <main className="app-main">
          <ReportsPage />
        </main>
        <footer className="app-footer">
          <div className="footer-inner">
            <div className="footer-brand">
              <U4RadLogo size={20} />
              <span className="footer-brand-name">U4Rad AI Vitals</span>
            </div>
            <p className="footer-copyright">
              &copy; {new Date().getFullYear()} U4Rad Technologies. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    );
  }

  return null;
}

export default App;